import React from 'react';
import classNames from 'classnames';
import { CSSTransition } from 'react-transition-group';
import * as math from 'mathjs';
import { Link } from 'react-router-dom';

import Input from 'components/input';
import Button from 'components/button';
import ButtonLoader from 'components/button-loader';
import Icon from 'icons';
import prettierNumber from 'utils/prettier-number';
import request from 'request';
import modalStyles from './components/create-modal.module.scss';
import Modal from './components/create-modal';
import styles from './styles.module.scss';
import { reverseDate } from 'utils/date-reverse';
import NavigateApplication from 'components/nav-app';
import collateral from './collateral';
import { transcriptCollateral } from 'utils/transcript';

const firstDayOfYear = new Date(new Date().getFullYear(), 0, 1);
const lastDayOfYear = new Date(new Date().getFullYear(), 11, 31);

const ApplicationCollateral = () => {
  const [from, setFrom] = React.useState<Date>(firstDayOfYear);
  const [to, setTo] = React.useState<Date>(lastDayOfYear);
  const [page, setPage] = React.useState(1);
  const [rowCount, setRowCount] = React.useState(1);
  const [newApplicationModal, setNewApplicationModal] = React.useState(false);
  const [pending, setPending] = React.useState(true);
  const resultRef = React.useRef<HTMLDivElement | null>(null);

  const [data, setData] = React.useState<collateral.result[]>([]);

  const handleFilter = async () => {
    const fd = reverseDate(from);
    const td = reverseDate(to);
    setPending(true);
    resultRef.current?.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
    setPage(1);
    request
      .get(`/applications/history?fd=${fd}&td=${td}&page=${page}&application_type=gold`)
      .then((res) => res.data)
      .then((d) => {
        setData(d.records);
        setRowCount(d.total_count);
      })
      .finally(() => setPending(false));
  };

  const handleClear = async () => {
    setFrom(firstDayOfYear);
    setTo(lastDayOfYear);
    const fd = reverseDate(firstDayOfYear);
    const td = reverseDate(lastDayOfYear);
    setPending(true);
    setPage(1);
    request
      .get(`/applications/history?fd=${fd}&td=${td}&page=${page}&application_type=gold`)
      .then((res) => res.data)
      .then((d) => {
        setData(d.records);
        setRowCount(d.total_count);
      })
      .finally(() => setPending(false));
  };

  React.useEffect(() => {
    const handleLoadRows = async () => {
      const fd = reverseDate(from);
      const td = reverseDate(to);
      setPending(true);
      resultRef.current?.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
      request
        .get(`/applications/history?fd=${fd}&td=${td}&page=${page}&application_type=gold`)
        .then((res) => res.data)
        .then((d) => {
          setData(d.records);
          setRowCount(d.total_count);
        })
        .finally(() => setPending(false));
    };
    handleLoadRows();
  }, [page]);

  return (
    <div className={styles.results} ref={resultRef}>
      <div className={styles.container}>
        <NavigateApplication />
        <div className={styles.tools}>
          <div className={styles.filterBox}>
            <div className={styles.input}>
              <Input title="От" value={from} type="date" onSetDate={(d) => setFrom(d)} />
            </div>
            <div className={styles.input}>
              <Input title="До" value={to} type="date" onSetDate={(d) => setTo(d)} />
            </div>
            <div className={styles.buttonGroup}>
              <Button pending={pending} onClick={handleFilter}>
                Фильтр
              </Button>
              <Button pending={pending} color="red" onClick={handleClear}>
                Сбросить
              </Button>
            </div>
          </div>
          <div className={styles.buttonGroup}>
            <Button color="green" onClick={() => setNewApplicationModal(true)}>
              Новая заявка
            </Button>
          </div>
        </div>
        {pending && (
          <div className={styles.tableLoading}>
            <div className={styles.tableLoadingRow}>
              <ButtonLoader />
            </div>
            <div className={styles.tableLoadingRow}>
              <ButtonLoader />
            </div>
            <div className={styles.tableLoadingRow}>
              <ButtonLoader />
            </div>
            <div className={styles.tableLoadingRow}>
              <ButtonLoader />
            </div>
            <div className={styles.tableLoadingRow}>
              <ButtonLoader />
            </div>
            <div className={styles.tableLoadingRow}>
              <ButtonLoader />
            </div>
            <div className={styles.tableLoadingRow}>
              <ButtonLoader />
            </div>
            <div className={styles.tableLoadingRow}>
              <ButtonLoader />
            </div>
            <div className={styles.tableLoadingRow}>
              <ButtonLoader />
            </div>
            <div className={styles.tableLoadingRow}>
              <ButtonLoader />
            </div>
            <div className={styles.tableLoadingRow}>
              <ButtonLoader />
            </div>
            <div className={styles.tableLoadingRow}>
              <ButtonLoader />
            </div>
            <div className={styles.tableLoadingRow}>
              <ButtonLoader />
            </div>
            <div className={styles.tableLoadingRow}>
              <ButtonLoader />
            </div>
            <div className={styles.tableLoadingRow}>
              <ButtonLoader />
            </div>
            <div className={styles.tableLoadingRow}>
              <ButtonLoader />
            </div>
          </div>
        )}
        {!pending && data.length !== 0 && (
          <div className={styles.table}>
            {data.map((item) => (
              <Link
                to={`/application/collateral/${item.id}/scoring`}
                className={styles.tableRowWrap}
                key={item.id}
              >
                <div className={styles.tableRow}>
                  <div className={styles.item}>
                    <span>ID заявки</span> {item.id}
                  </div>
                  <div className={styles.item}>
                    <span>Эксперт</span>
                    {item?.created_by.email}
                  </div>
                  <div className={styles.item}>
                    <span>Дата создания</span>
                    {new Date(item.created_at).toLocaleString()}
                  </div>
                  <div className={styles.item}>
                    <span>Запрашиваемая сумма</span>
                    {item.requested_amount
                      ? prettierNumber(item.requested_amount) +
                        ' ' +
                        transcriptCollateral.currency.getValueFromKey(item?.currency ?? '')
                      : '–'}
                  </div>
                  <div className={styles.item}>
                    <span>Длительность</span>
                    {item.duration} мес.
                  </div>
                  <div className={styles.item}>
                    <span>Процентная ставка</span>
                    {Math.round(item.int_rate)}%
                  </div>
                  <div
                    className={classNames(
                      styles.item,
                      item.is_approved && styles.green,
                      !item.is_approved && item.scoring_gold_log && styles.red,
                    )}
                  >
                    <span>Результат</span>
                    {item.scoring_gold_log === null
                      ? 'Неизвестно'
                      : item.is_approved
                      ? 'Одобрено'
                      : 'Отказано'}
                  </div>
                  <div className={styles.item}>
                    <span>Вероятность одобрения</span>
                    {item.scoring_gold_log?.prediction
                      ? `${Math.round(item.scoring_gold_log?.prediction)}%`
                      : '–'}
                  </div>
                  <div className={styles.item}>
                    <span>Порог</span>
                    {item.scoring_gold_log?.threshold
                      ? `${item.scoring_gold_log?.threshold * 100}%`
                      : '–'}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
        {!pending && data.length === 0 && <div className={styles.tableIsEmptyInfo}>Нет заявок</div>}
        {rowCount > 15 && (
          <div className={styles.pagination}>
            <button
              className={classNames(styles.arrow, page <= 1 && styles.disabled)}
              onClick={() => setPage(math.max(page - 1, 1))}
            >
              <Icon name="arrowLeft" />
            </button>
            <div className={styles.label}>
              {page} / {Math.ceil(rowCount / 15)}
            </div>
            <button
              className={classNames(styles.arrow, page * 15 >= rowCount && styles.disabled)}
              onClick={() => setPage(math.min(page + 1, math.ceil(rowCount / 15)))}
            >
              <Icon name="arrowRight" />
            </button>
          </div>
        )}
      </div>
      <CSSTransition
        in={newApplicationModal}
        timeout={150}
        unmountOnExit
        classNames={{
          enter: modalStyles.modalEnter,
          enterActive: modalStyles.modalEnterActive,
          exit: modalStyles.modalExit,
          exitActive: modalStyles.modalExitActive,
        }}
      >
        <Modal onClose={() => setNewApplicationModal(false)} />
      </CSSTransition>
    </div>
  );
};

export default ApplicationCollateral;
